import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { calcularProgressao, calcularPontosCiclo, calcularLimiarFuncionario, pontosParaAvaliacao, PONTOS_HISTORICO, aplicaRegraEspecial18Anos, PONTOS_REGRA_18_ANOS, type AvaliacaoSiadap, type PontosEscalasConfig } from '@/utils/progressaoUtils';
import { useAppSettings } from '@/context/AppSettingsContext';
import FuncionarioResumoCard, { type ResumoFuncionario } from './components/FuncionarioResumoCard';
import ResumoDetalheModal from './components/ResumoDetalheModal';
import ResumoCarreiraGroup from './components/ResumoCarreiraGroup';

export default function ResumoPage() {
  const navigate = useNavigate();
  const { funcionarios, loading: loadingFunc } = useFuncionarios();
  const { registosSiadap, registosHistorico, pontosDescontadosPorFuncionario, loadingSiadap } = useSiadap();
  const { settings } = useAppSettings();
  const [search, setSearch] = useState('');
  const [carreiraFilter, setCarreiraFilter] = useState('');
  const [sortBy, setSortBy] = useState<'nome' | 'pontos' | 'nivel' | 'avaliacoes'>('pontos');
  const [selectedResumo, setSelectedResumo] = useState<ResumoFuncionario | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'carreira'>('cards');

  const loading = loadingFunc || loadingSiadap;

  // Mapa de pontos por avaliação dinâmico (2025+)
  const pontosMap = useMemo<Partial<Record<AvaliacaoSiadap, number>>>(() => ({
    Excelente: settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom: settings.pontos_bom,
    Regular: settings.pontos_regular,
    Inadequado: settings.pontos_inadequado,
  }), [settings]);

  // Escalas completas para as 3 épocas — vindas das definições de administração
  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente: settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom: settings.pontos_bom,
      Regular: settings.pontos_regular,
      Inadequado: settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente: settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom: settings.pontos_bom_2023,
      Regular: settings.pontos_regular_2023,
      Inadequado: settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente:  settings.pontos_excelente_pre2023,
      Relevante:  settings.pontos_relevante_pre2023,
      Adequado:   settings.pontos_adequado_pre2023,
      Inadequado: settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom:         settings.pontos_adequado_pre2023,
      Regular:     settings.pontos_adequado_pre2023,
    },
  }), [settings]);

  const resumos = useMemo<ResumoFuncionario[]>(() => {
    if (funcionarios.length === 0) return [];
    const resumoMap: Record<string, ResumoFuncionario> = {};
    funcionarios.forEach((f) => {
      resumoMap[f.id] = {
        id: f.id,
        nome: f.nome,
        carreira: f.carreira,
        nivelAtual: f.nivelAtual,
        pontosAcumulados: 0,
        pontosDescontados: f.pontosDescontados ?? 0,
        pontosAtuaisCiclo: 0,
        registos: [],
      };
    });
    // SIADAP: usa pontosParaAvaliacao que respeita o período e as escalas dinâmicas
    registosSiadap.forEach((row) => {
      if (!resumoMap[row.funcionarioId]) return;
      const periodo = row.periodoAvaliacao ?? '';
      const anoMatch = periodo.match(/(\d{4})/);
      const ano = anoMatch ? parseInt(anoMatch[1], 10) : new Date().getFullYear();
      const pontos = pontosParaAvaliacao(row.avaliacao, periodo, pontosMap, escalas);
      resumoMap[row.funcionarioId].registos.push({ ano, avaliacao: row.avaliacao, pontuacaoChefe: row.pontosChefe, pontuacaoCCA: row.pontosCCA, pontos, fonte: 'siadap' });
    });
    // Histórico manual: usa escala do histórico (Relevante=4, Bom=3, Adequado=2)
    registosHistorico.forEach((row) => {
      if (!resumoMap[row.funcionarioId]) return;
      const pontos = PONTOS_HISTORICO[row.classificacaoServico] ?? 0;
      resumoMap[row.funcionarioId].registos.push({ ano: row.ano, avaliacao: row.classificacaoServico, pontuacaoChefe: row.pontuacaoChefe, pontuacaoCCA: row.pontuacaoCCA, pontos, fonte: 'historico' });
    });
    Object.values(resumoMap).forEach((r) => {
      const func = funcionarios.find((f) => f.id === r.id);
      const siadapDoFunc = registosSiadap.filter((s) => s.funcionarioId === r.id);
      const historicoDoFunc = registosHistorico.filter((h) => h.funcionarioId === r.id);
      // Usa a soma real das progressões como pontos descontados (fonte de verdade — tabela progressoes)
      const pontosDescontados = pontosDescontadosPorFuncionario[r.id] ?? 0;
      // Exceção PREPAV: se o toggle estiver ativo, os pontos manuais são SEMPRE somados
      const isPrepav = func?.excecaoPrepav === true;
      const temRegistos = siadapDoFunc.length > 0 || historicoDoFunc.length > 0;
      // Pontos manuais são SEMPRE somados
      const pontosManuais = func?.pontosAtuais ?? 0;
      // Usa calcularPontosCiclo como fonte única de verdade (igual à página de progressões)
      const totalLiquido = calcularPontosCiclo(
        siadapDoFunc,
        historicoDoFunc,
        pontosDescontados,
        pontosManuais,
        pontosMap,
        undefined,
        escalas
      );
      // pontosAcumulados = bruto sem descontos (para mostrar no detalhe)
      const bruto = r.registos.reduce((s, reg) => s + reg.pontos, 0);
      r.pontosAcumulados = bruto;
      r.pontosDescontados = pontosDescontados;
      r.pontosAtuaisCiclo = totalLiquido;
    });
    return Object.values(resumoMap);
  }, [funcionarios, registosSiadap, registosHistorico, pontosMap, escalas]);

  const carreiras = useMemo(() => Array.from(new Set(funcionarios.map((f) => f.carreira))).sort(), [funcionarios]);

  const filtered = useMemo(() => {
    let list = resumos.filter((r) => {
      const matchSearch = search === '' || r.nome.toLowerCase().includes(search.toLowerCase()) || r.id.includes(search);
      const matchCarreira = carreiraFilter === '' || r.carreira === carreiraFilter;
      return matchSearch && matchCarreira;
    });
    list = [...list].sort((a, b) => {
      if (sortBy === 'pontos') return b.pontosAcumulados - a.pontosAcumulados;
      if (sortBy === 'nivel') return b.nivelAtual - a.nivelAtual;
      if (sortBy === 'avaliacoes') return b.registos.length - a.registos.length;
      return a.nome.localeCompare(b.nome);
    });
    return list;
  }, [resumos, search, carreiraFilter, sortBy]);

  const totalPontos = resumos.reduce((s, r) => s + r.pontosAcumulados, 0);
  const mediaPontos = resumos.length > 0 ? (totalPontos / resumos.length).toFixed(1) : '—';
  const topFuncionario = resumos.length > 0 ? [...resumos].sort((a, b) => b.pontosAcumulados - a.pontosAcumulados)[0] : null;
  const comAvaliacoes = resumos.filter((r) => r.registos.length > 0).length;

  // Pontos por funcionário — usa calcularPontosCiclo como fonte única de verdade (igual à página home)
  const pontosPorFuncionario = useMemo(() => {
    const mapa: Record<string, {
      total: number;
      siadap: number;
      historico: number;
      limiar: number;
      limiarEfetivo: number;
      regraEspecial18Anos: boolean;
      periodo: 'pre2025' | 'pos2025' | 'misto';
      pontosPre2025: number;
      pontosPos2025: number;
    }> = {};
    funcionarios.forEach((f) => {
      const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);

      const limiarInfo = calcularLimiarFuncionario(
        siadapDoFunc,
        historicoDoFunc,
        settings.pontos_progressao,
        settings.pontos_progressao_pre2025,
        settings.pontos_progressao_2023,
        escalas
      );

      // Fonte única de verdade: calcularPontosCiclo (igual à página de progressões)
      // Usa a soma real das progressões como pontos descontados (fonte de verdade — tabela progressoes)
      const descontosProgressoes = pontosDescontadosPorFuncionario[f.id] ?? 0;
      const descontosManuais = f.pontosDescontados ?? 0;
      let pontosDescontados: number;
      if (descontosManuais > descontosProgressoes) {
        pontosDescontados = descontosManuais;
      } else if (descontosManuais === descontosProgressoes) {
        pontosDescontados = descontosProgressoes;
      } else {
        pontosDescontados = descontosProgressoes + descontosManuais;
      }
      // Exceção PREPAV: se o toggle estiver ativo, os pontos manuais são SEMPRE somados
      const isPrepavR = f.excecaoPrepav === true;
      const temRegistosR = siadapDoFunc.length > 0 || historicoDoFunc.length > 0;
      // Pontos manuais são SEMPRE somados
      const pontosManuais = f.pontosAtuais ?? 0;
      const total = calcularPontosCiclo(
        siadapDoFunc,
        historicoDoFunc,
        pontosDescontados,
        pontosManuais,
        pontosMap,
        undefined,
        escalas
      );

      const jaProgrediu = (pontosDescontadosPorFuncionario[f.id] ?? 0) > 0;
      const regraEspecial = aplicaRegraEspecial18Anos(f.dataEntradaFuncaoPublica, total, jaProgrediu, 2024, limiarInfo.limiar);
      const limiarEfetivo = regraEspecial ? PONTOS_REGRA_18_ANOS : limiarInfo.limiar;

      mapa[f.id] = {
        total,
        siadap: 0, // informativo apenas, não afeta o total
        historico: 0,
        limiar: limiarInfo.limiar,
        limiarEfetivo,
        regraEspecial18Anos: regraEspecial,
        periodo: limiarInfo.periodo,
        pontosPre2025: limiarInfo.pontosPre2025Total,
        pontosPos2025: limiarInfo.pontosPos2025Total,
      };
    });
    return mapa;
  }, [funcionarios, registosSiadap, registosHistorico, pontosMap, escalas, settings.pontos_progressao, settings.pontos_progressao_pre2025, settings.pontos_progressao_2023]);

  /** Agrupamento por carreira para o modo "por carreira" */
  const porCarreiraResumo = useMemo(() => {
    const grupos: Record<string, ResumoFuncionario[]> = {};
    filtered.forEach((r) => {
      if (!grupos[r.carreira]) grupos[r.carreira] = [];
      grupos[r.carreira].push(r);
    });
    return Object.entries(grupos).sort(([a, ga], [b, gb]) => {
      const eleA = ga.filter((r) => {
        const info = pontosPorFuncionario[r.id];
        const pontos = info?.total ?? 0;
        const limiar = info?.limiarEfetivo ?? info?.limiar ?? settings.pontos_progressao;
        return pontos >= limiar;
      }).length;
      const eleB = gb.filter((r) => {
        const info = pontosPorFuncionario[r.id];
        const pontos = info?.total ?? 0;
        const limiar = info?.limiarEfetivo ?? info?.limiar ?? settings.pontos_progressao;
        return pontos >= limiar;
      }).length;
      if (eleB !== eleA) return eleB - eleA;
      return a.localeCompare(b);
    });
  }, [filtered, pontosPorFuncionario, settings.pontos_progressao]);

  const progressaoPorCarreira = useMemo(() => {
    const map: Record<string, { elegivel: number; proximo: number; emProgresso: number; total: number }> = {};
    funcionarios.forEach((f) => {
      if (!map[f.carreira]) map[f.carreira] = { elegivel: 0, proximo: 0, emProgresso: 0, total: 0 };
      map[f.carreira].total += 1;
      const pontos = pontosPorFuncionario[f.id]?.total ?? 0;
      const limiar = pontosPorFuncionario[f.id]?.limiarEfetivo ?? pontosPorFuncionario[f.id]?.limiar ?? settings.pontos_progressao;
      const prog = calcularProgressao(pontos, f.nivelAtual, limiar);
      if (prog.status === 'pronto') map[f.carreira].elegivel += 1;
      else if (prog.status === 'proximo') map[f.carreira].proximo += 1;
      else map[f.carreira].emProgresso += 1;
    });
    return Object.entries(map).map(([carreira, stats]) => ({ carreira, ...stats }));
  }, [funcionarios, pontosPorFuncionario, settings.pontos_progressao]);

  return (
    <>
      <main className="flex-1 px-8 py-6 max-w-[1400px] w-full mx-auto">
        {/* PAGE HEADER */}
        <div className="mb-7">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
              <i className="ri-pie-chart-2-line text-white text-lg" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Análise</span>
              </div>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Resumo por Funcionário</h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">Pontos acumulados e progressão de nível — SIADAP e histórico manual</p>
            </div>
          </div>
        </div>

        {!loading && progressaoPorCarreira.length > 0 && (
          <div className="bg-white rounded-xl border border-zinc-100 p-5 mb-6">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg text-zinc-600 flex-shrink-0">
                  <i className="ri-bar-chart-grouped-line text-sm" />
                </div>
                <div>
                  <h2 className="text-[13px] font-bold text-zinc-800">Progressão por Carreira</h2>
                  <p className="text-[11px] text-zinc-400">Distribuição do estado de progressão por grupo profissional</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {[{ color: 'bg-emerald-500', label: 'Elegível' }, { color: 'bg-amber-400', label: 'Próximo' }, { color: 'bg-zinc-200', label: 'Em progresso' }].map((l) => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${l.color}`} />
                    <span className="text-[11px] text-zinc-500">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              {progressaoPorCarreira.map(({ carreira, elegivel, proximo, emProgresso, total }) => {
                const pctElegivel  = total > 0 ? (elegivel  / total) * 100 : 0;
                const pctProximo   = total > 0 ? (proximo   / total) * 100 : 0;
                const pctProgresso = total > 0 ? (emProgresso / total) * 100 : 0;
                return (
                  <div key={carreira}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-zinc-700">{carreira}</span>
                        <span className="text-[11px] text-zinc-400 bg-zinc-100 px-2 py-0.5 rounded-full">{total}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px]">
                        <span className="text-emerald-700 font-semibold">{elegivel} elegíveis</span>
                        <span className="text-amber-600">{proximo} próximos</span>
                        <span className="text-zinc-400">{emProgresso} em progresso</span>
                      </div>
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden flex">
                      {pctElegivel  > 0 && <div className="h-full bg-emerald-500" style={{ width: `${pctElegivel}%`  }} />}
                      {pctProximo   > 0 && <div className="h-full bg-amber-400"   style={{ width: `${pctProximo}%`   }} />}
                      {pctProgresso > 0 && <div className="h-full bg-zinc-200"    style={{ width: `${pctProgresso}%` }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filtros + toggle de vista */}
        <div className="flex flex-col md:flex-row gap-2.5 mb-4">
          <div className="relative flex-1">
            <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar por nome ou nº..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
            />
          </div>
          <select
            value={carreiraFilter}
            onChange={(e) => setCarreiraFilter(e.target.value)}
            className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
          >
            <option value="">Todas as Carreiras</option>
            {carreiras.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {viewMode === 'cards' && (
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
            >
              <option value="pontos">Mais pontos</option>
              <option value="nivel">Nível mais alto</option>
              <option value="avaliacoes">Mais avaliações</option>
              <option value="nome">Nome A-Z</option>
            </select>
          )}
          {/* Toggle de modo */}
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1 flex-shrink-0">
            <button
              onClick={() => setViewMode('cards')}
              className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-all cursor-pointer ${
                viewMode === 'cards' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <i className="ri-layout-grid-line text-sm" />
              Cards
            </button>
            <button
              onClick={() => setViewMode('carreira')}
              className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-all cursor-pointer ${
                viewMode === 'carreira' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <i className="ri-briefcase-line text-sm" />
              Por Carreira
            </button>
          </div>
        </div>

        <p className="text-[12px] text-zinc-400 mb-4">
          <span className="font-semibold text-zinc-600">{filtered.length}</span> de {resumos.length} funcionário(s)
          {viewMode === 'carreira' && porCarreiraResumo.length > 0 && (
            <span className="ml-2 text-zinc-300">· {porCarreiraResumo.length} carreira{porCarreiraResumo.length !== 1 ? 's' : ''}</span>
          )}
        </p>

        {loading ? (
          <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3 text-zinc-300">
            <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin" /></div>
            <p className="text-[13px] text-zinc-400">A carregar resumos...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3 text-zinc-300">
            <div className="w-12 h-12 flex items-center justify-center"><i className="ri-search-line text-4xl" /></div>
            <p className="text-[13px] text-zinc-400">Nenhum funcionário encontrado.</p>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((r) => (
              <div key={r.id} className="relative group">
                <FuncionarioResumoCard
                  resumo={r}
                  pontosAtuaisCiclo={pontosPorFuncionario[r.id]?.total ?? 0}
                  pontosSiadap={pontosPorFuncionario[r.id]?.siadap ?? 0}
                  pontosHistorico={pontosPorFuncionario[r.id]?.historico ?? 0}
                  limiar={pontosPorFuncionario[r.id]?.limiarEfetivo ?? pontosPorFuncionario[r.id]?.limiar ?? settings.pontos_progressao}
                  regraEspecial18Anos={pontosPorFuncionario[r.id]?.regraEspecial18Anos ?? false}
                  periodo={pontosPorFuncionario[r.id]?.periodo ?? 'pos2025'}
                  pontosPre2025={pontosPorFuncionario[r.id]?.pontosPre2025 ?? 0}
                  pontosPos2025={pontosPorFuncionario[r.id]?.pontosPos2025 ?? 0}
                  onClick={() => setSelectedResumo(r)}
                />
                {/* Botão de relatório individual */}
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/funcionarios/${r.id}/relatorio`); }}
                  className="whitespace-nowrap absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-white border border-zinc-200 rounded-lg text-zinc-400 hover:text-emerald-600 hover:border-emerald-300 transition-all opacity-0 group-hover:opacity-100 cursor-pointer z-10"
                  title="Ver relatório individual"
                >
                  <i className="ri-file-chart-line text-sm" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {porCarreiraResumo.map(([carreira, funcs]) => (
              <ResumoCarreiraGroup
                key={carreira}
                carreira={carreira}
                resumos={funcs}
                pontosPorFuncionario={pontosPorFuncionario}
                defaultLimiar={settings.pontos_progressao}
                onCardClick={(r) => setSelectedResumo(r)}
              />
            ))}
          </div>
        )}
      </main>



      {selectedResumo && (
        <ResumoDetalheModal resumo={selectedResumo} onClose={() => setSelectedResumo(null)} />
      )}
    </>
  );
}
